/**
 * Requests pipeline table with filters (creator, status, budget tier, created
 * range), cursor "load more", and CSV export. Row click opens RequestDetail.
 */

import React from 'react';
import { Button, EmptyState, Spinner, StatusPill, tableStyles } from '../ui';
import { STATUS_VARIANT, fmtDate, fmtWindow } from './shared';
import {
  STATUS_LABELS,
  TAKEOVER_STATUSES,
  TakeoverCreator,
  TakeoverRequest,
  TakeoverRequestStatus,
} from './types';

export interface RequestFiltersState {
  creatorId: string;
  status: '' | TakeoverRequestStatus;
  createdFrom: string;
  createdTo: string;
}

interface RequestsTableProps {
  requests: TakeoverRequest[];
  loading: boolean;
  nextCursor: string | null;
  filters: RequestFiltersState;
  creators: TakeoverCreator[];
  canManage: boolean;
  search: string;
  onSearchChange: (next: string) => void;
  onFiltersChange: (next: RequestFiltersState) => void;
  onOpen: (id: string) => void;
  onLoadMore: () => void;
  onExport: () => void;
  exporting: boolean;
}

const selectStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-1)',
  background: 'var(--bg-surface-1)',
  fontSize: 13,
  color: 'var(--fg-1)',
};

const emailDot = (state: string): string =>
  state === 'failed' ? 'var(--danger)' : state === 'sent' ? 'var(--success)' : state === 'skipped' ? 'var(--fg-4)' : 'var(--warning)';

const RequestsTable: React.FC<RequestsTableProps> = ({
  requests,
  loading,
  nextCursor,
  filters,
  creators,
  canManage,
  search,
  onSearchChange,
  onFiltersChange,
  onOpen,
  onLoadMore,
  onExport,
  exporting,
}) => {
  const searchLower = search.trim().toLowerCase();
  const visible = searchLower
    ? requests.filter(
        (r) =>
          r.brand.name.toLowerCase().includes(searchLower) ||
          r.contact.name.toLowerCase().includes(searchLower) ||
          r.contact.email.toLowerCase().includes(searchLower) ||
          r.creatorDisplayName.toLowerCase().includes(searchLower),
      )
    : requests;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search brand, contact, creator…"
          style={{ ...selectStyle, minWidth: 240 }}
        />
        <select
          value={filters.creatorId}
          onChange={(e) => onFiltersChange({ ...filters, creatorId: e.target.value })}
          style={selectStyle}
        >
          <option value="">All creators</option>
          {creators.map((c) => (
            <option key={c.id} value={c.id}>{c.displayName}</option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => onFiltersChange({ ...filters, status: e.target.value as RequestFiltersState['status'] })}
          style={selectStyle}
        >
          <option value="">All statuses</option>
          {TAKEOVER_STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
        <input
          type="date"
          value={filters.createdFrom}
          onChange={(e) => onFiltersChange({ ...filters, createdFrom: e.target.value })}
          style={selectStyle}
          title="Received from"
        />
        <input
          type="date"
          value={filters.createdTo}
          onChange={(e) => onFiltersChange({ ...filters, createdTo: e.target.value })}
          style={selectStyle}
          title="Received to"
        />
        <div style={{ flex: 1 }} />
        {canManage && (
          <Button variant="subtle" size="sm" onClick={onExport} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        )}
      </div>

      {loading && requests.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>
      ) : visible.length === 0 ? (
        <EmptyState
          title="No takeover requests"
          subtitle={searchLower ? 'Nothing matches your search.' : 'Requests submitted on creator pages will land here.'}
        />
      ) : (
        <div style={tableStyles.wrapScroll}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Received</th>
                <th style={tableStyles.th}>Creator</th>
                <th style={tableStyles.th}>Brand</th>
                <th style={tableStyles.th}>Contact</th>
                <th style={tableStyles.th}>Budget</th>
                <th style={tableStyles.th}>Window</th>
                <th style={tableStyles.th}>Status</th>
                <th style={tableStyles.th}>Emails</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => onOpen(r.id)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--bg-surface-2)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                >
                  <td style={tableStyles.td}>{fmtDate(r.createdAt)}</td>
                  <td style={tableStyles.td}>{r.creatorDisplayName}</td>
                  <td style={{ ...tableStyles.td, fontWeight: 600 }}>{r.brand.name}</td>
                  <td style={tableStyles.td}>
                    <div>{r.contact.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--fg-4)' }}>{r.contact.email}</div>
                  </td>
                  <td style={tableStyles.td}>{r.budgetTierLabel}</td>
                  <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>{fmtWindow(r.window)}</td>
                  <td style={tableStyles.td}>
                    <StatusPill variant={STATUS_VARIANT[r.displayStatus]}>{STATUS_LABELS[r.displayStatus]}</StatusPill>
                  </td>
                  <td style={tableStyles.td} title={`Advertiser: ${r.emailStatus.advertiser} · Creator: ${r.emailStatus.creator} · Internal: ${r.emailStatus.internal}`}>
                    <span style={{ display: 'inline-flex', gap: 4 }}>
                      {(['advertiser', 'creator', 'internal'] as const).map((k) => (
                        <span
                          key={k}
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: emailDot(r.emailStatus[k]),
                            display: 'inline-block',
                          }}
                        />
                      ))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nextCursor && !searchLower && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>
          <Button variant="subtle" size="sm" onClick={onLoadMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default RequestsTable;
